import type { UseMutationOptions } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";

import { jsonRequest } from "@/lib/network/json-request";

import { useLssMeeting } from "./use-lss-meeting";
import type { LssSlot } from "./use-lss-slots-by-request-id";

type LssSelectedSlot = Partial<Omit<LssSlot, "start">> & { start: Date };

interface RequestProps {
  isScheduled: boolean;
  requestId?: string;
  slot: LssSelectedSlot;
}

async function request({ requestId, slot, isScheduled }: RequestProps) {
  if (!requestId) {
    throw new Error("Request ID is required");
  }

  return jsonRequest(
    `${process.env.NEXT_PUBLIC_BOOKIT_BASE_URL}/appointments/${requestId}/datetime`,
    {
      method: isScheduled ? "PATCH" : "POST",
      body: { dateTime: slot.start },
    },
  );
}

type UseScheduleLssProps = Omit<
  UseMutationOptions<unknown, Error, LssSelectedSlot, unknown>,
  "mutationFn"
> & {
  requestId?: string;
};

export function useScheduleLss({ requestId, ...options }: UseScheduleLssProps) {
  const { data: currentMeeting } = useLssMeeting({ requestId });

  const status = currentMeeting?.status;

  const isMeetingScheduled =
    requestId && status ? ["INITIATED", "RESCHEDULED"].includes(status) : false;

  return useMutation({
    ...options,
    mutationFn: (slot: LssSelectedSlot) =>
      request({
        requestId,
        slot,
        isScheduled: Boolean(isMeetingScheduled),
      }),
  });
}
